<template>
	<div class="auto-job-card">
		<div class="auto-job-card__header">
			<app-checkbox
				:value="state.selected"
				:disabled="state.source.IsMandatory"
				@on-input="onSelectedInput"
			/>
			<span class="auto-job-card__header-label">{{ $t('autojob_create_job') }}</span>
			<app-tooltip
				v-if="errors.template"
				class="auto-job-card__icon"
				:text="$t(errors.template)"
			>
				<template #activator>
					<app-icon
						name="invalid"
						width="16px"
						color="#D05A4F"
					/>
				</template>
			</app-tooltip>
		</div>

		<div class="auto-job-card__text">
			<app-custom-textarea
				:model-value="state.text"
				:disabled="state.source.TextLocked"
				:placeholder="$t('job_message_placeholder')"
				:min-rows="1"
				:max-rows="6"
				@update:model-value="onTextInput"
			/>
			<app-tooltip
				v-if="errors.text"
				:class="{
					'auto-job-card__text-error': true,
					'auto-job-card__text-error--shifted': !!state.text && !state.source.TextLocked
				}"
				:text="$t(errors.text)"
			>
				<template #activator>
					<app-icon
						name="invalid"
						width="16px"
						color="#D05A4F"
					/>
				</template>
			</app-tooltip>
		</div>

		<div class="auto-job-card__field">
			<div class="auto-job-card__field-label">
				<span class="auto-job-card__field-title">{{ $t('autojob_author') }}</span>
				<app-tooltip
					v-if="state.source.AuthorLocked"
					class="auto-job-card__icon"
					:text="$t('autojob_field_locked')"
				>
					<template #activator>
						<app-icon
							name="lock"
							width="16px"
							color="#979797"
						/>
					</template>
				</app-tooltip>
				<app-tooltip
					v-if="errors.author"
					class="auto-job-card__icon"
					:text="$t(errors.author)"
				>
					<template #activator>
						<app-icon
							name="invalid"
							width="16px"
							color="#D05A4F"
						/>
					</template>
				</app-tooltip>
			</div>
			<app-select
				:model-value="state.authorKey"
				:items="authorOptions"
				:placeholder="$t('not_selected_label')"
				:disabled="state.source.AuthorLocked"
				description-key="description"
				description-tooltip-max-width="320px"
				searchable
				@update:model-value="onAuthorChange"
				@open="onAuthorsOpen"
			/>
		</div>

		<div class="auto-job-card__field">
			<div class="auto-job-card__field-label">
				<span class="auto-job-card__field-title">{{ $t('autojob_executor') }}</span>
				<app-tooltip
					v-if="state.source.ExecutorLocked"
					class="auto-job-card__icon"
					:text="$t('autojob_field_locked')"
				>
					<template #activator>
						<app-icon
							name="lock"
							width="16px"
							color="#979797"
						/>
					</template>
				</app-tooltip>
				<app-tooltip
					v-if="errors.executors"
					class="auto-job-card__icon"
					:text="$t(errors.executors)"
				>
					<template #activator>
						<app-icon
							name="invalid"
							width="16px"
							color="#D05A4F"
						/>
					</template>
				</app-tooltip>
			</div>
			<!-- Непустой dropdown-label выводит в поле имена через запятую: стандартный мультиселект показал бы «Выбрано N» -->
			<app-select
				:model-value="state.executorKeys"
				:items="executorOptions"
				:placeholder="$t('not_selected_label')"
				:dropdown-label="executorSummary || $t('not_selected_label')"
				:disabled="state.source.ExecutorLocked"
				description-key="description"
				description-tooltip-max-width="320px"
				multiple
				searchable
				@update:model-value="onExecutorsChange"
				@open="onExecutorsOpen"
			/>
		</div>

		<div class="auto-job-card__footer">
			<div class="auto-job-card__footer-item">
				<app-checkbox
					:value="state.needChecking"
					:disabled="state.source.NeedCheckingLocked"
					@on-input="onNeedCheckingInput"
				/>
				<span
					:class="{
						'auto-job-card__footer-label': true,
						'auto-job-card__footer-label--disabled': state.source.NeedCheckingLocked
					}"
				>{{ $t('job_with_report') }}</span>
			</div>

			<div class="auto-job-card__footer-item">
				<span
					v-if="state.source.DateLocked"
					class="auto-job-card__date auto-job-card__date--locked"
				>
					<app-icon
						name="clock"
						width="18px"
						color="#3F884F"
					/>
					<span>{{ formattedDate }}</span>
				</span>
				<app-date-string
					v-else
					:model-value="state.date"
					@update:model-value="onDateChange"
					@open="emit('calendar-toggle', true)"
					@close="emit('calendar-toggle', false)"
				>
					<template #trigger>
						<span
							:class="{
								'auto-job-card__date': true,
								'auto-job-card__date--empty': !state.date
							}"
						>
							<app-icon
								name="clock"
								width="18px"
								:color="state.date ? '#3F884F' : '#979797'"
							/>
							<span>{{ formattedDate || $t('ui_date_placeholder') }}</span>
						</span>
					</template>
				</app-date-string>
				<app-tooltip
					v-if="errors.date"
					class="auto-job-card__icon"
					:text="$t(errors.date)"
				>
					<template #activator>
						<app-icon
							name="invalid"
							width="16px"
							color="#D05A4F"
						/>
					</template>
				</app-tooltip>
			</div>

			<div class="auto-job-card__tags">
				<template v-if="lockedTags.length">
					<span
						v-for="tag in lockedTags"
						:key="`locked-${tag.Id}-${tag.Name}`"
						class="auto-job-card__tag auto-job-card__tag--locked"
					>#{{ tag.Name.toLowerCase().trim() }}</span>
					<app-tooltip
						class="auto-job-card__icon"
						:text="$t('autojob_field_locked')"
					>
						<template #activator>
							<app-icon
								name="lock"
								width="16px"
								color="#D2D2D2"
							/>
						</template>
					</app-tooltip>
				</template>
				<app-resizable-dropdown
					ref="$tagsDropdown"
					position="right"
					:close-by-click-outside="true"
					:max-width="640"
					:max-height="460"
					:content-z-index="10003"
					:mobile-title="$t('choose_tags')"
				>
					<template #activator>
						<app-tooltip :text="$t('file_add_tag')">
							<template #activator>
								<app-icon
									class="auto-job-card__tags-add"
									name="hash"
									width="18px"
									color="#979797"
									hover-color="#499259"
									cursor="pointer"
								/>
							</template>
						</app-tooltip>
					</template>
					<app-tag-selector
						:model-value="state.tags"
						:entity-type="TagEntityType.Job"
						mode="select"
						allow-create
						@update:model-value="onTagSelectorChange"
						@create-tag="onCreateTag"
						@close="$tagsDropdown?.close()"
					/>
				</app-resizable-dropdown>
				<span
					v-for="tag in state.tags"
					:key="tag.Id || tag.Name"
					class="auto-job-card__tag"
				>
					<span>{{ tag.Name.toLowerCase().trim() }}</span>
					<app-tooltip :text="$t('ui_tag_detach')">
						<template #activator>
							<app-icon
								class="auto-job-card__tag-delete"
								name="close-bold"
								width="12px"
								color="#D2D2D2"
								hover-color="#BA4C4C"
								cursor="pointer"
								@click="removeTag(tag)"
							/>
						</template>
					</app-tooltip>
				</span>
				<app-tooltip
					v-if="errors.tags"
					class="auto-job-card__icon"
					:text="$t(errors.tags)"
				>
					<template #activator>
						<app-icon
							name="invalid"
							width="16px"
							color="#D05A4F"
						/>
					</template>
				</app-tooltip>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	computed,
	ref
} from "vue";
import AppCheckbox from "@/components/ui-kit/checkbox/AppCheckbox.vue";
import AppCustomTextarea from "@/components/ui-kit/textarea/AppCustomTextarea.vue";
import AppDateString from "@/components/ui-kit/datepicker/AppDateString.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue";
import AppSelect from "@/components/ui-kit/select/AppSelect.vue";
import type { SelectModelValue } from "@/components/ui-kit/select/AppSelect.vue";
import AppTagSelector from "@/components/ui-kit/tag-selector/AppTagSelector.vue";
import AppTooltip from "@/components/ui-kit/tooltip/AppTooltip.vue";
import { TagBaseType, TagEntityType } from "@/models/etc/types";
import type { TagWithFrequency } from "@/models/interfaces/tags/tag-models";
import {
	buildAuthorOptions,
	buildExecutorOptions,
	buildExecutorSummary,
	formatAutoJobDate
} from "@/utils/autoJobTemplates";
import type {
	AutoJobTemplateErrors,
	AutoJobTemplatePatch,
	AutoJobTemplateState
} from "@/utils/autoJobTemplates";

type AutoJobTemplateCardProps = {
	state: AutoJobTemplateState;
	errors: AutoJobTemplateErrors;
};

const props = defineProps<AutoJobTemplateCardProps>();

const emit = defineEmits<{
	(e: 'update', patch: AutoJobTemplatePatch): void;
	(e: 'refresh-authors'): void;
	(e: 'refresh-executors'): void;
	(e: 'calendar-toggle', isOpen: boolean): void;
}>();

const $tagsDropdown = ref<InstanceType<typeof AppResizableDropdown> | null>(null);

const authorOptions = computed(() =>
	buildAuthorOptions(props.state.source.AuthorHolders, props.state.authorKey));
const executorOptions = computed(() =>
	buildExecutorOptions(props.state.source.ExectutorHolders, props.state.executorKeys));
const executorSummary = computed(() =>
	buildExecutorSummary(props.state.source.ExectutorHolders, props.state.executorKeys));

const lockedTags = computed(() => props.state.source.LockedTags ?? []);
const lockedTagNames = computed(() => new Set(lockedTags.value.map(tag => tag.Name.trim().toLowerCase())));

const formattedDate = computed(() => formatAutoJobDate(props.state.date));

const onSelectedInput = (value: boolean) => {
	emit('update', { selected: value });
};

const onTextInput = (value: string) => {
	emit('update', { text: value });
};

const onAuthorChange = (value: SelectModelValue) => {
	if (value == null || Array.isArray(value)) {
		return;
	}
	emit('update', { authorKey: String(value) });
};

// Правило против дублей «роль + её участник» применяет composable
const onExecutorsChange = (value: SelectModelValue) => {
	const keys = Array.isArray(value) ? value.map(String) : [];
	emit('update', { executorKeys: keys });
};

const onNeedCheckingInput = (value: boolean) => {
	emit('update', { needChecking: value });
};

const onDateChange = (value: Date | null) => {
	emit('update', { date: value });
};

// Списки перечитываем, когда пользователь открывает невалидное поле: например, в соседней
// вкладке добавили человека в роль.
const onAuthorsOpen = () => {
	if (props.errors.author || props.state.needRefresh.authors) {
		emit('refresh-authors');
	}
};

const onExecutorsOpen = () => {
	if (props.errors.executors || props.state.needRefresh.executors) {
		emit('refresh-executors');
	}
};

const withoutLocked = (tags: TagWithFrequency[]): TagWithFrequency[] =>
	tags.filter(tag => !lockedTagNames.value.has(tag.Name.trim().toLowerCase()));

const onTagSelectorChange = (nextTags: TagWithFrequency[]) => {
	emit('update', { tags: withoutLocked(nextTags) });
};

const onCreateTag = (name: string) => {
	const normalized = name.trim().toLowerCase();
	if (!normalized) {
		return;
	}
	const current = props.state.tags;
	if (lockedTagNames.value.has(normalized) || current.some(tag => tag.Name.toLowerCase().trim() === normalized)) {
		return;
	}
	const tempId = Math.min(0, ...current.map(tag => tag.Id)) - 1;
	emit('update', {
		tags: [...current, {
			Id: tempId,
			Name: normalized,
			Type: TagBaseType.Common,
			Frequency: 0,
			FullName: normalized,
		}]
	});
};

const removeTag = (tag: TagWithFrequency) => {
	emit('update', {
		tags: props.state.tags.filter(t =>
			t.Id !== tag.Id || t.Name.toLowerCase() !== tag.Name.toLowerCase()
		)
	});
};
</script>

<style scoped lang="scss">
@import "@/styles/color.scss";

.auto-job-card {
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding: 8px;
	border-radius: 8px;
	background-color: #F9F9F9;

	&__header {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: -4px;

		&-label {
			font-size: 14px;
			line-height: 18px;
			color: $color-text_main;
		}
	}

	&__icon,
	&__icon :deep(.app-tooltip__activator) {
		display: flex;
	}

	&__text {
		position: relative;

		:deep(.app-custom-textarea:disabled) {
			background-color: $background-white;
			color: $color-text-secondary;
		}

		:deep(.app-custom-textarea:disabled:hover) {
			border-color: $color-border;
		}

		&-error {
			position: absolute;
			top: 8px;
			right: 10px;
			display: flex;

			:deep(.app-tooltip__activator) {
				display: flex;
			}

			&--shifted {
				right: 34px;
			}
		}
	}

	&__field {
		display: flex;
		flex-direction: column;
		gap: 4px;

		&-label {
			display: flex;
			align-items: center;
			gap: 6px;
		}

		&-title {
			font-weight: 500;
			font-size: 12px;
			line-height: 16px;
			color: $color-text-secondary;
		}

		:deep(.app-select--disabled .app-select__trigger) {
			background-color: $background-white;
			color: $color-text_main;
		}

		:deep(.app-select--disabled .app-select__arrow) {
			opacity: 0.43;
		}
	}

	&__footer {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 8px 12px;

		&-item {
			display: flex;
			align-items: center;
			gap: 8px;
		}

		&-label {
			font-size: 14px;
			line-height: 18px;
			color: $color-text_main;

			&--disabled {
				color: $color-text-secondary;
			}
		}
	}

	&__date {
		display: flex;
		align-items: center;
		gap: 4px;
		font-size: 14px;
		line-height: 18px;
		color: $color-accent;
		cursor: pointer;

		&--empty {
			color: $color-text-secondary;
		}

		&--locked {
			opacity: 0.43;
			cursor: default;
		}
	}

	&__tags {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 4px;

		&-add {
			margin-top: -2px;
			margin-right: 2px;
		}
	}

	&__tag {
		display: inline-flex;
		align-items: center;
		font-size: 14px;
		line-height: 18px;
		color: $color-text-secondary;
		text-transform: lowercase;

		&--locked {
			color: $color-text_unavailable;
		}

		&-delete {
			margin: 0 0 0 2px;
		}
	}
}
</style>
